import { Box, BoxProps, Flex, Heading, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useEffect, useRef } from 'react';

import { Link } from '#features/routing';
import { Image } from '#features/ui';

type Game = {
  id: string;
  name: string | null;
  slug: string | null;
  previewImage: string | null;
};

type PopularGamesProps = {
  games?: Game[];
} & BoxProps;

const GameCard = ({ game }: { game: Game }) => (
  <Box
    position="relative"
    borderRadius="xl"
    overflow="hidden"
    flexShrink={0}
    w={{ base: '220px', md: '280px' }}
    h={{ base: '150px', md: '190px' }}
    cursor="pointer"
    border="1px solid"
    borderColor="whiteAlpha.100"
    transition="all 0.35s cubic-bezier(0.4, 0, 0.2, 1)"
    _hover={{
      borderColor: 'purple.400',
      transform: 'translateY(-4px)',
      '& .game-overlay': { opacity: 0.4 },
      '& .game-glow': { opacity: 1 },
    }}
  >
    {/* @ts-expect-error dynamic route param */}
    <Link to="/{-$locale}/$gameSlug" params={{ gameSlug: game.slug ?? '' }} style={{ display: 'block', width: '100%', height: '100%' }}>
      {game.previewImage && (
        <Image
          src={game.previewImage}
          alt={game.name ?? ''}
          width={400}
          style={{
            objectFit: 'cover',
            width: '100%',
            height: '100%',
            position: 'absolute',
            inset: 0,
          }}
        />
      )}
      <Box
        className="game-overlay"
        position="absolute"
        inset={0}
        background="linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.1) 100%)"
        transition="opacity 0.35s"
      />
      <Box
        className="game-glow"
        position="absolute"
        inset={0}
        opacity={0}
        transition="opacity 0.35s"
        style={{
          boxShadow: '0 0 30px rgba(75, 80, 230, 0.3), inset 0 0 30px rgba(75, 80, 230, 0.1)',
        }}
        borderRadius="xl"
        pointerEvents="none"
      />
      <Box position="absolute" bottom={4} left={4} right={4} zIndex={2}>
        <Text
          color="white"
          fontWeight="700"
          fontSize={{ base: 'md', md: 'lg' }}
          lineHeight="short"
          style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}
        >
          {game.name}
        </Text>
      </Box>
    </Link>
  </Box>
);

export const PopularGames = ({ games, ...boxProps }: PopularGamesProps) => {
  const { t } = useLingui();
  const viewportRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll driven by rAF on a natively-scrollable container, so touch
  // devices can swipe the carousel by hand (with native momentum) while the
  // auto-scroll keeps going. Manual interaction (touch/pointer/wheel) pauses
  // it; it resumes shortly after the user lets go. Desktop hover pauses too.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const SPEED = 60; // px per second
    const RESUME_DELAY = 1500; // ms of idle before auto-scroll resumes

    let frameId = 0;
    let lastTs = 0;
    let resumeAt = 0;
    let hovering = false;

    // The track holds the list twice; half the scroll width is one full copy,
    // so wrapping by that amount keeps the loop seamless in both directions.
    const wrap = () => {
      const half = el.scrollWidth / 2;
      if (half <= 0) return;
      if (el.scrollLeft >= half) el.scrollLeft -= half;
      else if (el.scrollLeft <= 0) el.scrollLeft += half;
    };

    const tick = (ts: number) => {
      const dt = lastTs ? (ts - lastTs) / 1000 : 0;
      lastTs = ts;
      if (!reduceMotion && !hovering && ts >= resumeAt) {
        el.scrollLeft += SPEED * dt;
      }
      wrap();
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);

    const pause = () => {
      resumeAt = performance.now() + RESUME_DELAY;
    };
    const onEnter = () => {
      hovering = true;
    };
    const onLeave = () => {
      hovering = false;
    };

    el.addEventListener('pointerdown', pause);
    el.addEventListener('touchstart', pause, { passive: true });
    el.addEventListener('touchmove', pause, { passive: true });
    el.addEventListener('wheel', pause, { passive: true });
    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);

    return () => {
      cancelAnimationFrame(frameId);
      el.removeEventListener('pointerdown', pause);
      el.removeEventListener('touchstart', pause);
      el.removeEventListener('touchmove', pause);
      el.removeEventListener('wheel', pause);
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [games?.length]);

  if (!games?.length) {
    return null;
  }

  const loop = [...games, ...games];

  return (
    <Box as="section" aria-label="Popular Games" w="100%" {...boxProps}>
      <Heading
        size={{ base: '2xl', md: '4xl' }}
        w="100%"
        textAlign="center"
        fontWeight="900"
        mb={2}
        as="h2"
        textTransform="uppercase"
        letterSpacing="-0.02em"
      >
        {t`popularGames`}
      </Heading>
      <Text textAlign="center" color="fg.muted" mb={8} fontSize="md">
        {t`v2.popularGames.subtitle`}
      </Text>
      <Box
        ref={viewportRef}
        // Keeps the rAF auto-scroll out of the router's scroll-restoration
        // tracking (see the scroll listener in client.tsx).
        data-scroll-restoration-ignore
        position="relative"
        w="100%"
        overflowX="auto"
        overflowY="hidden"
        css={{
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          overscrollBehaviorX: 'contain',
        }}
        style={{
          maskImage: 'linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)',
          WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)',
        }}
      >
        <Flex w="max-content" gap={4} py={2}>
          {loop.map((game, index) => (
            <GameCard key={`${game.id}-${index}`} game={game} />
          ))}
        </Flex>
      </Box>
    </Box>
  );
};
